"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import Image from "next/image"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { normalizeImageUrl } from "@lib/util/image-url"

const GAP_PX = 32
const TEXT_SECTION_PX = 140

export type VirtualizedGridItem = {
  id: string
  handle: string
  title: string
  thumbnail?: string | null
  images?: { url?: string | null }[]
  price?: string | null
}

type VirtualizedProductGridProps = {
  items: VirtualizedGridItem[]
}

const getColumns = (width: number) => {
  if (width >= 1024) return 3
  if (width >= 768) return 2
  return 1
}

const VirtualizedProductGrid = ({ items }: VirtualizedProductGridProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const rafRef = useRef<number | null>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const [scrollY, setScrollY] = useState(0)
  const [containerTop, setContainerTop] = useState(0)
  const [viewportHeight, setViewportHeight] = useState(0)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const updateMeasurements = () => {
      const rect = container.getBoundingClientRect()
      setContainerWidth(container.clientWidth)
      setContainerTop(rect.top + window.scrollY)
      setScrollY(window.scrollY)
      setViewportHeight(window.innerHeight)
    }

    updateMeasurements()

    const resizeObserver = new ResizeObserver(updateMeasurements)
    resizeObserver.observe(container)

    const onScroll = () => {
      if (rafRef.current !== null) return
      rafRef.current = window.requestAnimationFrame(() => {
        setScrollY(window.scrollY)
        rafRef.current = null
      })
    }

    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", updateMeasurements)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", updateMeasurements)
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current)
      }
    }
  }, [])

  const { columns, itemWidth, rowHeight, totalRows } = useMemo(() => {
    const cols = getColumns(containerWidth)
    const width = containerWidth > 0 ? (containerWidth - GAP_PX * (cols - 1)) / cols : 0
    const height = width + TEXT_SECTION_PX
    const rows = cols > 0 ? Math.ceil(items.length / cols) : 0
    return {
      columns: cols,
      itemWidth: width,
      rowHeight: height + GAP_PX,
      totalRows: rows,
    }
  }, [containerWidth, items.length])

  const { startRow, endRow } = useMemo(() => {
    if (!rowHeight) {
      return { startRow: 0, endRow: totalRows }
    }

    const viewportTop = scrollY
    const viewportBottom = scrollY + viewportHeight
    const buffer = 2

    const relativeTop = Math.max(0, viewportTop - containerTop)
    const relativeBottom = Math.max(0, viewportBottom - containerTop)

    const start = Math.max(0, Math.floor(relativeTop / rowHeight) - buffer)
    const end = Math.min(totalRows, Math.ceil(relativeBottom / rowHeight) + buffer)

    return { startRow: start, endRow: end }
  }, [containerTop, rowHeight, scrollY, totalRows, viewportHeight])

  const visibleItems = useMemo(() => {
    if (!columns || !rowHeight) return items

    const startIndex = startRow * columns
    const endIndex = Math.min(items.length, endRow * columns)

    return items.slice(startIndex, endIndex).map((item, index) => {
      const globalIndex = startIndex + index
      const row = Math.floor(globalIndex / columns)
      const col = globalIndex % columns
      return { item, row, col, index: globalIndex }
    })
  }, [columns, endRow, items, rowHeight, startRow])

  if (!items.length) {
    return null
  }

  return (
    <div ref={containerRef} className="relative">
      <div style={{ height: totalRows * rowHeight }} />
      <div className="absolute inset-0">
        {visibleItems.map(({ item, row, col, index }) => {
          const image = normalizeImageUrl(item.thumbnail || item.images?.[0]?.url || null)
          const top = row * rowHeight
          const left = col * (itemWidth + GAP_PX)

          return (
            <div
              key={item.id}
              className="absolute"
              style={{
                top,
                left,
                width: itemWidth,
              }}
            >
              <LocalizedClientLink
                href={`/products/${item.handle}`}
                className="group block bg-cream border border-beige rounded-lg overflow-hidden hover-lift"
              >
                <div className="relative bg-beige-light overflow-hidden aspect-square">
                  {image ? (
                    <Image
                      src={image}
                      alt={item.title}
                      fill
                      sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                      loading={index < columns * 2 ? "eager" : "lazy"}
                      className="object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-brown-light">
                      No image
                    </div>
                  )}
                </div>

                <div className="p-4 flex flex-col gap-3">
                  <p className="text-lg font-serif font-bold text-foreground mb-2 line-clamp-1">
                    {item.title}
                  </p>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-x-2" style={{ color: "#a8372a" }}>
                      {item.price && <span className="text-base font-semibold">{item.price}</span>}
                    </div>

                    <span
                      className="px-4 py-2 text-cream rounded-full text-xs font-medium hover:brightness-[1.1] transition-all"
                      style={{ backgroundColor: "#a8372a" }}
                    >
                      View details
                    </span>
                  </div>
                </div>
              </LocalizedClientLink>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default VirtualizedProductGrid
